import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * ImageZoom — main gallery image with:
 *  - hover magnifier lens (desktop)
 *  - click / tap to open fullscreen lightbox with keyboard nav
 */
export default function ImageZoom({ images, activeIndex, onIndexChange, alt }) {
  const [pos, setPos] = useState({ x: 50, y: 50, active: false });
  const [lightbox, setLightbox] = useState(false);
  const wrapRef = useRef(null);
  const closeBtnRef = useRef(null);
  const openerRef = useRef(null);

  const src = images[activeIndex];

  const onMove = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100));
    setPos({ x, y, active: true });
  };

  useEffect(() => {
    if (!lightbox) return;
    openerRef.current = document.activeElement;
    setTimeout(() => closeBtnRef.current?.focus(), 60);
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(false);
      if (e.key === 'ArrowRight') onIndexChange((activeIndex + 1) % images.length);
      if (e.key === 'ArrowLeft')  onIndexChange((activeIndex - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      openerRef.current?.focus?.();
    };
  }, [lightbox, activeIndex, images.length, onIndexChange]);

  return (
    <>
      <div
        ref={wrapRef}
        className="relative aspect-[3/4] bg-muted overflow-hidden cursor-zoom-in group"
        onMouseEnter={() => setPos((p) => ({ ...p, active: true }))}
        onMouseLeave={() => setPos((p) => ({ ...p, active: false }))}
        onMouseMove={onMove}
        onClick={() => setLightbox(true)}
        role="button"
        tabIndex={0}
        aria-label="Open image in full view"
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setLightbox(true)}
        data-testid="pdp-image-zoom"
      >
        <img
          src={src}
          srcSet={buildSrcSet(src)}
          sizes="(max-width: 768px) 100vw, 50vw"
          alt={alt}
          width="1200"
          height="1600"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        {/* Zoom lens */}
        <div
          className="hidden md:block absolute pointer-events-none rounded-full border border-foreground/30 shadow-lg backdrop-blur-[1px] overflow-hidden bg-background/30 transition-opacity duration-300"
          style={{
            width: 200,
            height: 200,
            left: `calc(${pos.x}% - 100px)`,
            top: `calc(${pos.y}% - 100px)`,
            opacity: pos.active ? 1 : 0,
            backgroundImage: `url(${src})`,
            backgroundSize: '260% 260%',
            backgroundPosition: `${pos.x}% ${pos.y}%`,
          }}
          aria-hidden
        />
        <div className="absolute right-4 bottom-4 h-9 w-9 inline-flex items-center justify-center bg-background/85 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <ZoomIn className="h-4 w-4" />
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
          {lightbox && (
            <motion.div
              className="fixed inset-0 z-[100] bg-background/98 backdrop-blur-xl flex flex-col"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.85, 0, 0.15, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label="Image lightbox"
            data-testid="pdp-lightbox"
          >
            <div className="flex items-center justify-between px-6 md:px-12 h-16 border-b border-border">
              <p className="overline text-muted-foreground tabular">
                {String(activeIndex + 1).padStart(2, '0')} / {String(images.length).padStart(2, '0')}
              </p>
              <button
                ref={closeBtnRef}
                aria-label="Close lightbox"
                onClick={() => setLightbox(false)}
                data-testid="pdp-lightbox-close"
                className="h-10 w-10 inline-flex items-center justify-center hover:bg-foreground/5"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 relative flex items-center justify-center px-2 md:px-16 py-6 overflow-hidden">
              <motion.img
                key={activeIndex}
                src={src}
                alt={alt}
                initial={{ opacity: 0, scale: 0.99 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.85, 0, 0.15, 1] }}
                className="max-h-full max-w-full object-contain"
                draggable={false}
              />
              {images.length > 1 && (
                <>
                  <button
                    aria-label="Previous image"
                    onClick={(e) => { e.stopPropagation(); onIndexChange((activeIndex - 1 + images.length) % images.length); }}
                    className="absolute left-4 md:left-8 h-12 w-12 inline-flex items-center justify-center bg-background/70 hover:bg-background border border-border transition-colors duration-500"
                    data-testid="pdp-lightbox-prev"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    aria-label="Next image"
                    onClick={(e) => { e.stopPropagation(); onIndexChange((activeIndex + 1) % images.length); }}
                    className="absolute right-4 md:right-8 h-12 w-12 inline-flex items-center justify-center bg-background/70 hover:bg-background border border-border transition-colors duration-500"
                    data-testid="pdp-lightbox-next"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
            {/* Thumbnail strip */}
            {images.length > 1 && (
              <div className="border-t border-border px-6 md:px-12 py-4 flex gap-2 justify-center overflow-x-auto">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => onIndexChange(i)}
                    className={`h-16 w-12 shrink-0 overflow-hidden transition-opacity duration-500 ${i === activeIndex ? 'opacity-100 ring-1 ring-foreground' : 'opacity-50 hover:opacity-100'}`}
                    aria-label={`Show image ${i + 1}`}
                  >
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

// Build a responsive srcset for Unsplash-hosted images by swapping the ?w= param
function buildSrcSet(src) {
  if (!src || !src.includes('images.unsplash.com')) return undefined;
  const widths = [400, 600, 800, 1000, 1200, 1600];
  return widths.map((w) => {
    const next = src.includes('w=') ? src.replace(/w=\d+/, `w=${w}`) : src + (src.includes('?') ? '&' : '?') + `w=${w}`;
    return `${next} ${w}w`;
  }).join(', ');
}
